import axios, {
  type CreateAxiosDefaults,
  type AxiosResponse,
  type AxiosError,
  type Method,
  type InternalAxiosRequestConfig,
} from 'axios';
import { toast } from 'sonner';
import { logger } from './logger';
import { expireAuthSession, refreshAccessToken } from './auth-session';

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

function shouldRefreshAccessToken(error: AxiosError, originalRequest?: RetryableRequestConfig): boolean {
  return (
    error?.response?.status === 401 &&
    Boolean(originalRequest) &&
    !originalRequest?._retry &&
    !originalRequest?.url?.startsWith('/auth/')
  );
}

const baseOptions: CreateAxiosDefaults = {
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  withCredentials: true,
};

const client = axios.create({
  ...baseOptions,
  headers: {
    'Content-Type': 'application/json',
  },
});

const clientMultiForm = axios.create({
  ...baseOptions,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

const responseErrorInterceptor = async (error: AxiosError) => {
  const originalRequest = error.config as RetryableRequestConfig | undefined;

  if (shouldRefreshAccessToken(error, originalRequest)) {
    if (!originalRequest) {
      throw error;
    }

    originalRequest._retry = true;

    try {
      await refreshAccessToken();
      return await axios.request(originalRequest);
    } catch (refreshError) {
      expireAuthSession();
      throw refreshError;
    }
  }

  if (error?.response?.status === 401) {
    expireAuthSession();
  }
  if (error?.response?.status === 403) {
    toast('У вас нет прав для выполнения этого запроса');
  }
  return Promise.reject(error);
};

[client, clientMultiForm].forEach((axiosInstance) => {
  // axiosInstance.interceptors.request.use(requestInterceptor);
  axiosInstance.interceptors.response.use((response) => response, responseErrorInterceptor);
});

export interface RequestOptions {
  method: Method;
  url: string;
  data?: unknown;
  params?: unknown;
  headers?: Record<string, string>;
  isMultipart?: boolean;
  priority?: 'high' | 'low';
  unwrapData?: boolean;
}

export interface BackendResponse<T> {
  status: string;
  data: T;
  pagination?: {
    totalPages: number;
    totalItems: number;
  };
}

export interface BackendErrorResponse {
  status: string;
  message: {
    message: string;
  };
  data?: {
    error?: string;
    message?: string;
    timestamp?: string;
  };
}

export interface ApiError {
  message: string | undefined;
  status: number | undefined;
  response: BackendErrorResponse | undefined;
  request: {
    method: Method;
    url: string;
    data?: unknown;
    params?: unknown;
    headers?: Record<string, string>;
  };
}

const MAX_CONCURRENT_REQUESTS = 4;
let activeRequests = 0;
const highPriorityQueue: (() => Promise<unknown>)[] = [];
const lowPriorityQueue: (() => Promise<unknown>)[] = [];

async function processQueue() {
  if (activeRequests >= MAX_CONCURRENT_REQUESTS) {
    return;
  }
  const task = highPriorityQueue.shift() ?? lowPriorityQueue.shift();
  if (!task) {
    return;
  }
  activeRequests++;
  await task();
  activeRequests--;
  processQueue();
}

export function api<T>(
  options: Omit<RequestOptions, 'unwrapData'> & { unwrapData: false },
): Promise<BackendResponse<T>>;
export function api<T>(options: Omit<RequestOptions, 'unwrapData'> & { unwrapData?: true }): Promise<T>;
export function api<T>({
  method,
  url,
  data,
  params,
  headers,
  isMultipart = false,
  priority = 'low',
  unwrapData = true,
}: RequestOptions): Promise<T | BackendResponse<T>> {
  return new Promise((resolve, reject) => {
    const task = async () => {
      try {
        const activeClient = isMultipart ? clientMultiForm : client;
        const requestHeaders = { ...headers };

        if (priority === 'high') {
          requestHeaders['X-Request-Priority'] = 'high';
        }

        const response: AxiosResponse<BackendResponse<T>> = await activeClient.request({
          method,
          url,
          data,
          params,
          withCredentials: true,
          headers: requestHeaders,
        });

        if (unwrapData === false) {
          resolve(response.data);
        } else {
          resolve(response.data.data);
        }
      } catch (error) {
        const axiosError = error as AxiosError<BackendErrorResponse>;
        const backendResponseData = axiosError.response?.data;
        const backendMessage = backendResponseData?.message?.message;
        logger.error('API request error', { error, backendResponseData, backendMessage });
        const apiErrorLabel = backendMessage ?? `${method} request failed for ${url}`;
        const apiErrorData: ApiError = {
          message: backendMessage,
          status: axiosError.response?.status,
          response: backendResponseData,
          request: {
            method,
            url,
            data,
            params,
            headers,
          },
        };

        logger.error(apiErrorLabel, apiErrorData);

        reject(new Error(apiErrorLabel, { cause: apiErrorData }));
      }
    };

    if (priority === 'high') {
      highPriorityQueue.push(task);
    } else {
      lowPriorityQueue.push(task);
    }

    processQueue();
  });
}
